'use client';

import { ArrowBack, ArrowForward, Save } from '@mui/icons-material';
import { Box, Button, CircularProgress, Step, StepLabel, Stepper, TextField, Typography } from '@mui/material';
import { useLazyQuery, useMutation } from '@parkchirp/api-client';
import { useSearchParams } from 'next/navigation';
import React, { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';

import { CREATE_LEASING_RIGHT } from '../../../mutations';
import { VERIFY_LEASE_ACCOUNT_ID } from '../../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { CountAndRate, LeasingRight, Subtenant } from '../../../types/leases';
import {
	getEmptyCountAndRate,
	getGridRows,
	isOwnerPortal,
	leasingRightErrorMessage,
	stopAllEdits,
	validateLeaseRequiredFields,
	validateLeasingCounts,
	validateLeasingRightDates,
} from '../../../utils';
import { LeaseCountAndRate } from './leaseCountAndRate';
import { getLeaseCountAndRateColumns } from './leaseDetails';
import { LeaseGeneral } from './leaseGeneral';
import { Subtenants } from './subtenants';

type CreateLeaseFormProps = {
	leasingRight: LeasingRight;
	setLeasingRight: Dispatch<SetStateAction<LeasingRight>>;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
	getUrlToSubscriptionPage: (...x: any[]) => string;
};

const steps = {
	account: {
		id: 0,
		label: 'Select Account',
	},
	lease: {
		id: 1,
		label: 'Add Lease Details',
	},
	subtenants: {
		id: 2,
		label: 'Add Subtenants',
		optional: true,
	},
	countAndRate: {
		id: 3,
		label: 'Add Count & Rate',
	},
};

export const CreateLeaseForm: FC<CreateLeaseFormProps> = ({
	leasingRight,
	setLeasingRight,
	setShowDialog,
	setShowSnackbar,
	getUrlToSubscriptionPage,
}) => {
	const searchParams = useSearchParams();
	const accountIdParam = searchParams.get('accountId');
	const [currentStep, setCurrentStep] = useState(0);
	const [accountId, setAccountId] = useState<string>(accountIdParam);
	const [childrenGridApiRef, setChildrenGridApiRef] = useState<any>({});
	const [countAndRate, setCountAndRate] = useState<CountAndRate>(null);
	const [rateId, setRateId] = useState('');
	const [sublotId, setSublotId] = useState<number>(null);
	const [subtenants, setSubtenants] = useState<Subtenant[]>([]);

	const countAndRateColumns = useMemo(() => {
		if (!countAndRate) return [];

		return getLeaseCountAndRateColumns(
			childrenGridApiRef[countAndRate?.id],
			countAndRate,
			'America/Chicago', // Timezone doesn't matter since all we need from the columns is the field names
			true,
			false,
		);
	}, [childrenGridApiRef, countAndRate]);

	const [verifyLeaseAccountId, { loading: getFacilityLoading }] = useLazyQuery(VERIFY_LEASE_ACCOUNT_ID);
	const [createLeasingRight, { loading: saveInProgress }] = useMutation(CREATE_LEASING_RIGHT);

	const handleAccountIdSubmit = useCallback(async () => {
		const sanitizedAccountId = Number(accountId.replace(/\D/g, '')); // Remove all non-numbers

		const { data, error } = await verifyLeaseAccountId({
			variables: { accountId: sanitizedAccountId },
			context: { apiVersion: 'v2' },
		});

		if (error || !data) {
			console.log(error);
			setShowSnackbar({
				message: error ? leasingRightErrorMessage(error) : 'Internal Server Error',
				severity: 'error',
			});
			return;
		}

		setLeasingRight((prev: LeasingRight) => ({
			...prev,
			accountId: sanitizedAccountId,
			subscriptionId: data.verifyLeaseAccountId.subscriptionId,
			rightName: data.verifyLeaseAccountId.subscriptionName,
			facilityId: data.verifyLeaseAccountId.facilityId,
		}));

		setCurrentStep((prev) => prev + 1);
	}, [accountId, verifyLeaseAccountId, setLeasingRight, setShowSnackbar]);

	const handleNext = useCallback(() => {
		try {
			validateLeaseRequiredFields(leasingRight);

			const invalidDates = validateLeasingRightDates(leasingRight.commencementDate, leasingRight.expirationDate);
			if (invalidDates) throw new Error(invalidDates);
		} catch (e) {
			setShowSnackbar({
				message: e.message,
				severity: 'error',
			});
			return;
		}

		setCountAndRate(getEmptyCountAndRate(leasingRight));
		setCurrentStep((prev) => prev + 1);
	}, [leasingRight, setShowSnackbar]);

	function updateQueryParam(key: string, value: string | number | null) {
		const url = new URL(window.location.href);
		if (isOwnerPortal) {
			const newPath = `/lease-detail/${value}`;
			window.location.href = newPath;
		} else {
			const newPath = `/lease-detail/?id=${value}`;
			url.searchParams.set(key, String(value));
			window.location.href = newPath;
		}
	}

	const handleSave = useCallback(async () => {
		const gridApiRef = childrenGridApiRef[countAndRate?.id];

		stopAllEdits({
			saveChanges: true,
			gridApiRef,
			columns: countAndRateColumns,
		});

		const dataRows = getGridRows(gridApiRef);
		const updatedHistory = dataRows.map((row) => {
			const updatedRow = structuredClone(row);
			if (String(updatedRow.id).includes('new')) delete updatedRow.id;
			return updatedRow;
		});

		let leasingRightId: number = null;
		try {
			if (!rateId) throw new Error('Please select a rate');
			if (!sublotId) throw new Error('Please select a sublot');

			const invalidCounts = validateLeasingCounts({ leasingCountAndRateHistory: updatedHistory });
			if (invalidCounts) throw new Error(invalidCounts);

			try {
				const { data } = await createLeasingRight({
					variables: {
						...leasingRight,
						leasingCountAndRates: [
							{
								rateId,
								sublotId,
								leasingCountAndRateHistory: updatedHistory,
							},
						],
						buildings: (leasingRight.buildings ?? []).map(({ id }) => id),
						subtenants: subtenants.map((s) => ({ accountId: s.accountId, type: s.type })),
					},
					context: { apiVersion: 'v2' },
				});
				leasingRightId = data.createLeasingRight.id;
			} catch (e) {
				console.log(e);
				throw new Error(leasingRightErrorMessage(e));
			}

			setShowSnackbar({
				message: 'Saved successfully',
				severity: 'success',
				autoHideDuration: 5000,
			});

			updateQueryParam('id', leasingRightId);
		} catch (e) {
			setShowSnackbar({
				message: e.message,
				severity: 'error',
				autoHideDuration: 8000,
			});
		}
	}, [
		childrenGridApiRef,
		countAndRate?.id,
		countAndRateColumns,
		createLeasingRight,
		leasingRight,
		rateId,
		setShowSnackbar,
		sublotId,
		subtenants,
	]);

	const renderNavigationButtons = useCallback(
		() => (
			<Box display='flex' justifyContent='space-between' margin='2em'>
				{currentStep !== 0 && (
					<Button
						variant='contained'
						startIcon={<ArrowBack />}
						onClick={() => setCurrentStep((prev) => prev - 1)}
					>
						Back
					</Button>
				)}
				{[steps.lease.id, steps.subtenants.id].includes(currentStep) && (
					<Button variant='contained' endIcon={<ArrowForward />} onClick={handleNext}>
						Next
					</Button>
				)}
				{currentStep === steps.countAndRate.id &&
					(saveInProgress ? (
						<CircularProgress />
					) : (
						<Button variant='contained' startIcon={<Save />} onClick={handleSave}>
							Save
						</Button>
					))}
			</Box>
		),
		[currentStep, handleNext, handleSave, saveInProgress],
	);

	return (
		<Box>
			<Stepper activeStep={currentStep} alternativeLabel>
				{Object.keys(steps).map((step) => (
					<Step key={steps[step].id}>
						<StepLabel
							{...(steps[step].optional && {
								optional: <Typography variant='caption'>Optional</Typography>,
							})}
						>
							{steps[step].label}
						</StepLabel>
					</Step>
				))}
			</Stepper>

			<Box mt='2em'>
				{currentStep === steps.account.id && (
					<Box display='flex' justifyContent='center' alignItems='center' gap='1em' marginTop='5em'>
						<TextField
							id='select-account'
							label='Account ID'
							name='accountId'
							value={accountId || ''}
							onChange={(e) => setAccountId(e.target.value)}
							inputProps={{ 'data-cy': 'account-id-input' }}
						/>
						{getFacilityLoading ? (
							<CircularProgress />
						) : (
							<Button variant='contained' onClick={handleAccountIdSubmit}>
								Submit
							</Button>
						)}
					</Box>
				)}

				{currentStep === steps.lease.id && (
					<LeaseGeneral
						leasingRight={leasingRight}
						setLeasingRight={setLeasingRight}
						refetchLeasingRight={() => null}
						resetLeasingRight={() => null}
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
						setIsLeaseGeneralEditing={() => null}
						isLeaseGeneralEditing
						isLeaseDetailEditInProgress
						newMode
					/>
				)}

				{currentStep === steps.subtenants.id && (
					<Subtenants
						subtenants={subtenants}
						setSubtenants={setSubtenants}
						leasingRight={leasingRight}
						leaseNewMode
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
						getUrlToSubscriptionPage={getUrlToSubscriptionPage}
					/>
				)}

				{currentStep === steps.countAndRate.id && (
					<LeaseCountAndRate
						countAndRate={countAndRate}
						leasingRight={leasingRight}
						refetchLeasingRight={() => null}
						handleCancelNewMode={() => null}
						editMode
						setEditMode={() => null}
						pageEditInProgress
						conversionRightEditModes={{}}
						setConversionRightEditModes={() => null}
						setChildrenGridApiRef={setChildrenGridApiRef}
						rateId={rateId}
						setRateId={setRateId}
						sublotId={sublotId}
						setSublotId={setSublotId}
						leaseNewMode
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
					/>
				)}

				{renderNavigationButtons()}
			</Box>
		</Box>
	);
};
